import React from 'react'
import { Link } from 'react-router-dom'
import { ADMIN_ROUTES } from '../../../../constants'
import { formatYesNo } from '../../../../utils/formatters'

/*
 * The detail route key is composite (orionFirmId, orionRegistrationId) — we
 * can't link by a single id. URL helper centralizes the construction so column
 * cells, header links, and any future cross-references stay in sync.
 */
export const getDetailPath = (row) =>
  `${ADMIN_ROUTES.REGISTRATIONS}/${row.orionFirmId}/${row.orionRegistrationId}`

const longNameCell = ({ row }) => {
  const reg = row.original
  return (
    <Link
      onClick={(e) => e.stopPropagation()}
      to={getDetailPath(reg)}
    >
      {reg.longName || `Registration ${reg.orionRegistrationId}`}
    </Link>
  )
}

const yesNoCell = (field) => ({ row }) => formatYesNo(row.original[field])

/*
 * Sortable columns are limited to the indexed PK fields and modifiedDate.
 * Other columns are explicitly disabled to avoid generating slow ORDER BY
 * queries against unindexed text columns (orionFirmId/orionRegistrationId
 * are part of the PK, modifiedDate is indexed via the migration).
 */
export const defaultColumnConfig = {
  columns: [
    {
      Header: 'Orion Firm Name',
      accessor: 'orionFirmName',
      id: 'orionFirmName',
      disableSortBy: true
    },
    {
      Header: 'Orion Registration ID',
      accessor: 'orionRegistrationId',
      id: 'orionRegistrationId'
    },
    {
      Header: 'Long Name',
      accessor: 'longName',
      id: 'longName',
      Cell: longNameCell,
      disableSortBy: true
    },
    {
      Header: 'First Name',
      accessor: 'firstName',
      id: 'firstName',
      disableSortBy: true
    },
    {
      Header: 'Last Name',
      accessor: 'lastName',
      id: 'lastName',
      disableSortBy: true
    },
    {
      Header: 'Orion Household ID',
      accessor: 'orionClientId',
      id: 'orionClientId',
      disableSortBy: true
    },
    {
      Header: 'Orion Registration Type ID',
      accessor: 'orionRegistrationTypeId',
      id: 'orionRegistrationTypeId',
      disableSortBy: true
    },
    {
      Header: 'Orion Sleeve',
      accessor: 'orionSleeve',
      id: 'orionSleeve',
      Cell: yesNoCell('orionSleeve'),
      disableSortBy: true
    },
    {
      Header: 'Is Active Sleeve',
      accessor: 'isActiveSleeve',
      id: 'isActiveSleeve',
      Cell: yesNoCell('isActiveSleeve'),
      disableSortBy: true
    },
    {
      Header: 'Is Active',
      accessor: 'isActive',
      id: 'isActive',
      Cell: yesNoCell('isActive'),
      disableSortBy: true
    }
  ],
  defaultSort: [
    { id: 'orionRegistrationId', desc: false }
  ]
}

const TEXT_SEARCH_FIELDS = ['lastName', 'longName', 'firstName']

/*
 * Adapt the raw OperationalTable state into the server query envelope expected
 * by the search-registrations handler. Keeping this pure makes it trivial to
 * unit-test.
 */
export const buildSearchBody = ({
  pageIndex,
  pageSize,
  sort,
  searchText,
  activeOnly
}) => {
  const body = {
    skip: pageIndex * pageSize,
    take: pageSize,
    filters: {
      isActive: activeOnly ? true : null
    }
  }

  if (sort && sort.length > 0) {
    body.sort = sort.map(({ id, desc }) => ({
      field: id,
      dir: desc ? 'desc' : 'asc'
    }))
  }

  if (searchText && searchText.trim().length > 0) {
    body.textSearch = TEXT_SEARCH_FIELDS.reduce((acc, field) => {
      acc[field] = [{ op: 'contains', value: searchText.trim() }]
      return acc
    }, {})
  }

  return body
}
